<script setup>
import { ref, computed } from 'vue'
import { parseStringElements } from './yongsinUsage'

const props = defineProps({
  yongTimeChartData: [String, Array],
  heeTimeChartData:  [String, Array],
  yongFiveChartData: String,
  heeFiveChartData:  String,
  isMobile: {
    type: Boolean,
    default: false,
  }
})

/* 12지지 + 시간 매핑 */
const BRANCH = [
  { ko:'자', hanja:'子', time1:'23-01', time2:'23:00-01:00', five:'five02' },
  { ko:'축', hanja:'丑', time1:'01-03', time2:'01:00-03:00', five:'five05' },
  { ko:'인', hanja:'寅', time1:'03-05', time2:'03:00-05:00', five:'five03' },
  { ko:'묘', hanja:'卯', time1:'05-07', time2:'05:00-07:00', five:'five03' },
  { ko:'진', hanja:'辰', time1:'07-09', time2:'07:00-09:00', five:'five05' },
  { ko:'사', hanja:'巳', time1:'09-11', time2:'09:00-11:00', five:'five01' },
  { ko:'오', hanja:'午', time1:'11-13', time2:'11:00-13:00', five:'five01' },
  { ko:'미', hanja:'未', time1:'13-15', time2:'13:00-15:00', five:'five05' },
  { ko:'신', hanja:'申', time1:'15-17', time2:'15:00-17:00', five:'five04' },
  { ko:'유', hanja:'酉', time1:'17-19', time2:'17:00-19:00', five:'five04' },
  { ko:'술', hanja:'戌', time1:'19-21', time2:'19:00-21:00', five:'five05' },
  { ko:'해', hanja:'亥', time1:'21-23', time2:'21:00-23:00', five:'five02' },
]

/** 필터 상태 */
const activeFilter = ref('all')

/** 범례 버튼 토글 */
function toggleY() {
  activeFilter.value = activeFilter.value === 'y' ? 'all' : 'y'
}
function toggleH() {
  activeFilter.value = activeFilter.value === 'h' ? 'all' : 'h'
}

/* 오행 → 에너지키 */
const elemKey = (s) => {
  const m = String(s ?? '').match(/[목화토금수]/)
  const map = { 목:'wood', 화:'fire', 토:'earth', 금:'metal', 수:'water' }
  return map[m?.[0]] ?? null
}

/* 에너지 색 */
const energyColors = {
  fire:'#c23535',
  earth:'#db9823', 
  wood:'#5e9f1c', 
  metal:'#949494', 
  water:'#404040',
  inactive:'#ecf0f1',
}

/* 지지 문자열 파싱 */
const BRANCH_CHAR_RE = /[자축인묘진사오미신유술해]/g
function parseBranchTokens(input) {
  const tokens = parseStringElements(input)
  const out = []
  for (const t of tokens) {
    const m = String(t).match(BRANCH_CHAR_RE)
    if (m) out.push(...m)
  }

  return Array.from(new Set(out))
}

/* 오행키/지지 */
const yKey = computed(() => elemKey(props.yongFiveChartData))
const hKey = computed(() => elemKey(props.heeFiveChartData))
const ySet = computed(() => new Set(parseBranchTokens(props.yongTimeChartData)))
const hSet = computed(() => new Set(parseBranchTokens(props.heeTimeChartData))) 

/* 해당 지지 인덱스에서 활성 타입 */
function getActiveEnergyTypeByIndex(i) {
  const ko = BRANCH[i].ko
  if (activeFilter.value === 'y') {
    return ySet.value.has(ko) ? yKey.value : null
  }
  if (activeFilter.value === 'h') {
    return hSet.value.has(ko) ? hKey.value : null
  }
  if (ySet.value.has(ko)) return yKey.value
  if (hSet.value.has(ko)) return hKey.value
  return null
}


/* 도넛 세그먼트 */
const hoveredIndex = ref(null)

const timeSegments = computed(() => {
  const centerX = 160, centerY = 160
  const innerRadius = 65, outerRadius = 105
  const segs = []

  for (let i = 0; i < 12; i++) {
    const startAngle = (i * 30 - 90) * (Math.PI / 180)
    const endAngle   = ((i + 1) * 30 - 90) * (Math.PI / 180)

    const x1 = centerX + innerRadius * Math.cos(startAngle)
    const y1 = centerY + innerRadius * Math.sin(startAngle)
    const x2 = centerX + outerRadius * Math.cos(startAngle)
    const y2 = centerY + outerRadius * Math.sin(startAngle)
    const x3 = centerX + outerRadius * Math.cos(endAngle)
    const y3 = centerY + outerRadius * Math.sin(endAngle)
    const x4 = centerX + innerRadius * Math.cos(endAngle)
    const y4 = centerY + innerRadius * Math.sin(endAngle)

    const path = `M ${x1} ${y1} L ${x2} ${y2} A ${outerRadius} ${outerRadius} 0 0 1 ${x3} ${y3} L ${x4} ${y4} A ${innerRadius} ${innerRadius} 0 0 0 ${x1} ${y1} Z`
    const textRadius = (innerRadius + outerRadius) / 2
    const textAngle  = startAngle + (endAngle - startAngle) / 2
    const textX = centerX + textRadius * Math.cos(textAngle)
    const textY = centerY + textRadius * Math.sin(textAngle)

    const activeType = getActiveEnergyTypeByIndex(i)
    segs.push({
      idx: i,
      time1: BRANCH[i].time1,
      path, textX, textY,
      isActive: !!activeType,
      // color: activeType ? energyColors[activeType] : energyColors.inactive,
      fiveClass: activeType ? BRANCH[i].five : 'inactive',
    })
  }
  return segs
})

/** 지지 , 구분 */
const joinWithComma = (s) => [...String(s ?? '')].join(', ');

/* 범례 텍스트 */
function legendTextFromSet(set) {
  const items = BRANCH.filter(b => set.has(b.ko)).map(b => `${b.ko}(${b.time1})`)
  return items.join(', ')
}
const yLegend = computed(() => ({
  elem: yKey.value,
  color: energyColors[yKey.value] || '#ddd',
  text: joinWithComma(props.yongTimeChartData),
}))
const hLegend = computed(() => ({
  elem: hKey.value,
  color: energyColors[hKey.value] || '#ddd',
  text: joinWithComma(props.heeTimeChartData),
}))

/* 우측 리스트 */
const rightList = computed(() =>
  BRANCH.map((b, i) => {
    let highlight = null
    if (activeFilter.value === 'y') {
      highlight = ySet.value.has(b.ko) ? 'y' : null
    } else if (activeFilter.value === 'h') {
      highlight = hSet.value.has(b.ko) ? 'h' : null
    } else {
      highlight = ySet.value.has(b.ko) ? 'y' : (hSet.value.has(b.ko) ? 'h' : null)
    }

    const t = highlight === 'y' ? yKey.value : (highlight === 'h' ? hKey.value : null)

    return {
      idx: i,
      branchKo: b.ko,
      branchHanja: b.hanja,
      time2: b.time2,
      color: t ? energyColors[t] : null,
      highlight,
      five: b.five,
    }
  })
)


const getEnergyTypeName = (type) => {
  const names = { fire:'화 (火)', earth:'토 (土)', wood:'목 (木)', metal:'금 (金)', water:'수 (水)' }
  return names[type] || '기타'
}

// 지지별 오행 클래스
const BRANCH_FIVE_CLASS = {
  자:'five02', 해:'five02',
  인:'five03', 묘:'five03',
  오:'five01', 사:'five01',
  진:'five05', 술:'five05', 축:'five05', 미:'five05',
  신:'five04', 유:'five04',
}
const fiveClassFor = (ko) => BRANCH_FIVE_CLASS[ko] || null

const rowsCount = computed(() => Math.ceil(rightList.value.length / 2))
const gridRowsStyle = computed(() => ({
  gridTemplateRows: `repeat(${rowsCount.value}, auto)`,
}))

/** 한자 <-> 한글 변환 */
const langMode = ref('chn')
const changeLanguage = () =>{
  if (langMode.value === 'chn') langMode.value = 'kor'
  else langMode.value = 'chn'
}

</script>

<template>
  <div 
    class="energy-chart-container"
    :class="{mobile: isMobile}">


    <div 
      class="energy-chart-top"
      :class="{mobile: isMobile}">
      <div class="energy-chart-wrapper">
        <!-- <div class="energy-chart__chart-tit">임시 타이틀</div> -->
        <svg width="220" height="220" viewBox="45 45 230 230" class="energy-chart">
          <g v-for="seg in timeSegments" :key="seg.idx">
            <path
              :d="seg.path"
              :class="['segment', seg.fiveClass, { 'is-inactive': !seg.isActive }]"
              stroke="#fff" stroke-width="2"
              class="segment"
              @mouseover="hoveredIndex = seg.idx"
              @mouseleave="hoveredIndex = null"
            /> <!-- :fill="seg.color" -->
            <text
              :x="seg.textX" :y="seg.textY"
              text-anchor="middle" dominant-baseline="middle"
              font-size="11"
              :fill="seg.isActive ? '#fff' : '#999'"
              :font-weight="seg.isActive ? 'bold' : 'normal'"
              class="energy-time__text"
            >
              {{ seg.time1 }}
            </text>
          </g>

          <circle cx="160" cy="160" r="65" fill="white" stroke="#ddd" stroke-width="1"/>
          <text x="160" y="160" text-anchor="middle" font-size="18" font-weight="bold" fill="#333">24시간</text>
          <text x="160" y="180" text-anchor="middle" font-size="12" fill="#666">오행 에너지</text>
        </svg>

        <div v-if="hoveredIndex !== null" class="energy-chart__tooltip">
          <strong>{{ BRANCH[hoveredIndex].time2 }}</strong><br>
          {{ BRANCH[hoveredIndex].ko }} 시
          <!-- <span v-if="getActiveEnergyTypeByIndex(hoveredIndex)">
            선택된 시간에만 보여줄 데이터 표시하려면 이곳에
          </span> -->
        </div>
      </div>
          <!-- 버튼 -->
    <div 
      v-if="isMobile"
      class="tp-chart__btn-wrapper">
      <div 
        class="tp-chart__btn"
        :class="{mobile: isMobile}">
        <button
          class="yh__legend-btn"
          :data-active="activeFilter === 'y'"
          @click="toggleY"
          aria-pressed="legend === '용신'"
        >
          용신
        </button>
        <button
          class="yh__legend-btn"
          :data-active="activeFilter === 'h'"
          @click="toggleH"
          aria-pressed="legend === '희신'"
        >
          희신
        </button>
      </div>
      <div class="tp-chart__legend">
        <div 
          v-if="activeFilter === 'y'"
          class="tp-chart__legend-item">
          <div class="energy-chart__legend-color" :style="{ background: yLegend.color }"></div>
          <span>{{ getEnergyTypeName(yKey) }}</span>
          <span>( {{ yLegend.text }} )</span>
        </div>
        <div 
          v-if="activeFilter === 'h'"
          class="tp-chart__legend-item">
          <div class="energy-chart__legend-color" :style="{ background: hLegend.color }"></div>
          <span>{{ getEnergyTypeName(hKey) }}</span>
          <span>( {{ hLegend.text }} )</span>
        </div>
      </div>
    </div>

      <!-- 우측 리스트 -->
       <div class="energy-right__wrapper">
        <!-- <div class="energy-chart__chart-tit">임시 타이틀</div> -->
        <div class="energy-right-box" :style="gridRowsStyle">
          <div
            v-for="row in rightList"
            :key="row.idx"
            class="energy-right-box__item"
            :class="{
              'is-inactive': !row.highlight,
              [row.five]: !!row.highlight
            }"
            @click="changeLanguage()"
          >
            <!-- <span class="branch">{{ row.branchHanja }}</span>
            <span class="label" :style="{ background: row.color || '#e5e7eb' }">
              {{ row.branchKo }}시
            </span> -->
          <span
            class="txt-round xl chnkor yongsin"
            :class="[ 
              fiveClassFor(row.branchKo), { 'is-inactive': !row.highlight } 
              ]"
          >
            <em
              v-if="langMode === 'chn'"
              class="chn yongsin">
              {{ row.branchHanja }}
            </em>
            <em
              v-if="langMode === 'kor'"
              class="chn yongsin">
              {{ row.branchKo }}
            </em>
            <!-- <em class="txt-main yongsin">
              {{ row.branchKo }}
            </em> -->
          </span>
            <span class="slot">{{ row.time2 }}</span>
          </div>
        </div>
       </div>
    </div>
    <!-- 버튼 -->
    <div
      v-if="!isMobile"
      class="tp-chart__btn-wrapper">
      <div 
        class="tp-chart__btn"
        :class="{mobile: isMobile}">
        <button
          class="yh__legend-btn"
          :data-active="activeFilter === 'y'"
          @click="toggleY"
          aria-pressed="legend === '용신'"
        >
          용신
        </button>
        <button
          class="yh__legend-btn"
          :data-active="activeFilter === 'h'"
          @click="toggleH"
          aria-pressed="legend === '희신'"
        >
          희신
        </button>
      </div>
      <div class="tp-chart__legend">
        <div 
          v-if="activeFilter === 'y'"
          class="tp-chart__legend-item">
          <div class="energy-chart__legend-color" :style="{ background: yLegend.color }"></div>
          <span>{{ getEnergyTypeName(yKey) }}</span>
          <span>( {{ yLegend.text }} )</span>
        </div>
        <div 
          v-if="activeFilter === 'h'"
          class="tp-chart__legend-item">
          <div class="energy-chart__legend-color" :style="{ background: hLegend.color }"></div>
          <span>{{ getEnergyTypeName(hKey) }}</span>
          <span>( {{ hLegend.text }} )</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.txt-round.xl.yongsin{
  width: 24px;
  height: 24px;
  border-radius: 5px;
  background-image: var(--five-bg, none);
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  &.is-inactive { --five-bg: none; }
  &.five01:not(.is-inactive) { --five-bg: url("@manseImages/skyDay_tue.png"); }
  &.five02:not(.is-inactive) { --five-bg: url("@manseImages/skyDay_wed.png"); }
  &.five03:not(.is-inactive) { --five-bg: url("@manseImages/skyDay_thu.png"); }
  &.five04:not(.is-inactive) { --five-bg: url("@manseImages/skyDay_fri.png"); }
  &.five05:not(.is-inactive) { --five-bg: url("@manseImages/skyDay_sat.png"); }
  .chn.yongsin{
    font-size: 16px;
    left: 5px;
    top: 4px;
  }
  .txt-main.yongsin{
    font-size: 8px;
  }
}
</style>
